import { Separator } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ConfidentialityLevel } from '../../../GlobalConst/globalEnums';
import { mapEnumToOptions } from '../../../helpers/HelperMethods';
import InfoTooltip from '../../formFields/InfoTooltip';
import BooleanDropdown from '../SiteEditElements/BooleanDropdown';
import CustomDropDown from '../SiteEditElements/CustomDropDown';
import CustomTextField from '../SiteEditElements/CustomTextField';
import SecuredComponent from '../SiteEditElements/SecuredComponent';
import TabChangedDialog from '../TabChangedDialog';

const SolutionCompliance = ({ solution, disabled, onUpdate, isEditEnabled, isSaveEnabled, btnEdit, btnSave, messagePanel, showTabSwitchDialog, onDiscardDialog, onSaveDialog, onCancelDialog }) => {

    const currentUser = useSelector((state) => state.user.currentUser)
    const myOwnSolution = [solution.primaryOwner, solution.secondaryOwner].some(x => x && x.userId === currentUser.id) || solution.isOwnedViaServiceLeads || solution.isOwnedViaEnvironmentOwner

    const [childrenValidation, setChildrenValidation] = useState({})
    const [isChildrenUpdateValid, setIsChildrenUpdateValid] = useState(true)

    useEffect(() => {
        setIsChildrenUpdateValid(Object.keys(childrenValidation).reduce((value, key) => {
            return value && childrenValidation[key];
        }, true))
    }, [childrenValidation])

    const onUpdateChildrenValidation = (key, isValid) => {
        setChildrenValidation(valid => ({ ...valid, [key]: isValid }))
    }

    const tradeComplianceTooltip =
        <div>
            <p>
                PowerApps is not a suitable platform if you are working with Military or Dual Use documentation, or some very specific document types, including bills of materials (BOM), technical drawings or CAD Files. You will normally know if you are working with any of these, however you can find a more detailed set of examples here.
            </p>
            <p>
                Technical data, in the context of trade compliance, would include product specifications, CAD drawings or other related information which would allow the partial or full recreation of a product. 
            </p>
            <p>
                 Military products (also called defense articles) are weapons and ammunition of strategic significance, military vehicles, military equipment, military supplies, military technology/technical data, paramilitary equipment, and prototypes or spare parts for the aforementioned items.
            </p>
            <p>
                To ascertain whether any of the technical data is controlled it will be necessary to confer with the application owner or the business representative for the application. Typically this will refer to products which require a license, from the relevant authorities, to export from the country of origin to intended destinations. 
            </p>
        </div>

    const editor =
        <div className="site-editor">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={twoColumnsContainer}>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Privacy (ROP) <InfoTooltip content='Does the application you manage process Personal Data? If yes, provide Id of Record of Privacy' /></label>
                            <CustomTextField
                                defaultValue={solution.privacy}
                                disabled={disabled}
                                onUpdate={(v) => onUpdate('privacy', v)}
                                required={false}
                                onValidation={(v) => onUpdateChildrenValidation('privacy', v)}
                            />
                        </div>
                    </SecuredComponent>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Data classification</label>
                            <CustomDropDown
                                options={mapEnumToOptions(ConfidentialityLevel)}
                                defaultValue={solution.confidentiality}
                                disabled={disabled}
                                onUpdate={(v) => onUpdate('confidentiality', v)}
                                required={true}
                                onValidation={(v) => onUpdateChildrenValidation('confidentiality', v)}
                            />
                        </div>
                    </SecuredComponent>
                </div>
                <div style={twoColumnsContainer}>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Icofr / SOX compliance</label>
                            <BooleanDropdown
                                defaultValue={solution.icofrCompliance}
                                disabled={disabled}
                                onUpdate={(v) => onUpdate('icofrCompliance', v)}
                                onValidation={(v) => onUpdateChildrenValidation('icofrCompliance', v)}
                            />
                        </div>
                    </SecuredComponent>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Trade compliance <InfoTooltip content={tradeComplianceTooltip} /></label>
                            <BooleanDropdown
                                defaultValue={solution.tradeCompliance}
                                disabled={disabled}
                                onUpdate={(v) => onUpdate('tradeCompliance', v)}
                                onValidation={(v) => onUpdateChildrenValidation('tradeCompliance', v)}
                            />
                        </div>
                    </SecuredComponent>
                </div>
                <div>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <label className='item-title'>Infosec Security Assessment <InfoTooltip content='Id of Security Assessment – reference to Archer'/></label>
                        <CustomTextField
                            defaultValue={solution.infoSec}
                            disabled={disabled}
                            onUpdate={(v) => onUpdate('infoSec', v)}
                        />
                    </SecuredComponent>
                </div>
            </div>
        </div>

    const editPanel = 
        <div>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: '0 20px'}}>
                <SecuredComponent readSecurityLevel='Administrator'
                    editSecurityLevel='Administrator'
                    unlimitedAccess={myOwnSolution}>
                    <div style={{ width: '100%', marginTop: '20px', display: "flex", justifyContent: "space-between"}}>
                        {isEditEnabled() && btnEdit}
                        {!disabled && btnSave(isSaveEnabled() && isChildrenUpdateValid)}
                    </div>
                </SecuredComponent>
            </div>
            {messagePanel}
        </div>

    return (
        <div>
            {editor}
            <Separator />
            {editPanel}
            <TabChangedDialog
                isSaveEnabled={isSaveEnabled() && isChildrenUpdateValid}
                showDialog={showTabSwitchDialog}
                onDiscardDialog={onDiscardDialog}
                onSaveDialog={onSaveDialog}
                onCancelDialog={onCancelDialog}
            />
        </div>
    )
}

const twoColumnsContainer = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 20
}

const twoColumnsSingleCol = {
    //width: '295px'
    flex: 1,
    overflow: 'hidden'
}

export default SolutionCompliance;


// WEBPACK FOOTER //
// ./src/components/EditSite/PowerAppEditElements/SolutionCompliance.js